import moment from 'moment'

export const parseExtraContent = (value) => {
  try {
    const fields = typeof value === 'string' ? JSON.parse(value) : value
    return Array.isArray(fields) ? fields.filter(field => typeof field?.label === 'string') : []
  } catch {
    return []
  }
}

export const extraFieldKey = (label) => {
  const key = label.toLowerCase()
  return ({
    "doctor's name": 'doctor_name',
    "doctor's phone": 'doctor_number',
    "doctor's address": 'doctor_address',
  })[key] || key
}

export const getExtraContent = (fields, label) =>
  fields.find(field => extraFieldKey(field.label) === extraFieldKey(label))?.content || ''

export const groupClaimEvidence = (response) => {
  const records = response?.data ?? response
  const items = records?.evidence_id ? [records] : Object.values(records || {})
  const sections = new Map()

  items.filter(item => item?.evidence_id).forEach(item => {
    const isDocument = Boolean(item.evidence_document_id)
    const metadata = isDocument ? item.evidence || {} : item
    const existing = sections.get(item.evidence_id)
    sections.set(item.evidence_id, {
      ...existing,
      ...metadata,
      evidence_id: item.evidence_id,
      documents: [
        ...(existing?.documents || []),
        ...(isDocument ? [item] : Object.values(item.documents || {})),
      ],
    })
  })

  return [...sections.values()]
}

const MATERNITY_EVIDENCE_ID = '00499e10-33cc-11e7-80f2-cbd5f9baca14'
const isRequired = value => value === true || String(value).toLowerCase() === 'true'

export const evidenceFields = (section, doc = {}) => {
  const dates = Number(section.date_expire) === 1
    ? [
      { key: 'valid_start', label: 'Valid From', type: 'date', isDate: true, required: true },
      ...(section.evidence_id === MATERNITY_EVIDENCE_ID ? [] : [
        { key: 'valid_until', label: 'Valid Until', type: 'date', isDate: true, required: true },
      ]),
    ] : []
  const metadata = parseExtraContent(section.extra_fields)
  const existing = parseExtraContent(doc.extra_content ?? section.extra_content)
  const extras = [...metadata]
  existing.forEach(field => {
    if (!extras.some(extra => extraFieldKey(extra.label) === extraFieldKey(field.label))) {
      extras.push({ ...field, required: false })
    }
  })
  return [...dates, ...extras.map(field => ({
    ...field, key: extraFieldKey(field.label), isDate: false,
    type: field.type || 'text', required: isRequired(field.required),
  }))]
}

export const evidenceDates = (section, values) => ({
  valid_start: Number(section.date_expire) === 1 ? values.valid_start || null : null,
  valid_until: Number(section.date_expire) === 1 && section.evidence_id !== MATERNITY_EVIDENCE_ID
    ? values.valid_until || null : null,
})

export const validateEvidence = (section, values, { doc, file, upload = false } = {}) => {
  const fields = evidenceFields(section, doc)
  for (const field of fields) {
    if (field.isDate && doc?.evidence_document_status === 'accepted') continue
    const value = String(values[field.key] ?? '').trim()
    if (field.required && !value) return `${field.label} is required.`
    if (field.type === 'date' && value && !moment(value, 'YYYY-MM-DD', true).isValid()) {
      return `${field.label} must be a valid calendar date.`
    }
  }
  const dates = evidenceDates(section, values)
  if (doc?.evidence_document_status !== 'accepted' && dates.valid_start && dates.valid_until && dates.valid_until < dates.valid_start) {
    return 'Valid Until must be on or after Valid From.'
  }
  if (upload && !file) return 'Please select a file first.'
  return ''
}

export const evidenceApiError = (data, fallback) => {
  const details = Object.values(data?.errors || {}).flat().filter(value => typeof value === 'string')
  return [data?.message, ...details].filter(Boolean).join(' ') || fallback
}
